import React, { useEffect, useRef, useState } from "react";
import { formFixture } from "./FormFixture";
import "./hirearchy.css";
import config from "../../../helper/config";
import { customerConstants } from "../../../helper/constants.js";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

// Retrieve customer flag
const getCustomerFeatureFlag = () => {
  return config.customerConfigs.featureFlag;
};

let tabTextColor;
const customer = getCustomerFeatureFlag();
switch (customer) {
  case customerConstants.EXO_CUSTOMER1:
    tabTextColor = "var(--maincolor)";
    break;
  case customerConstants.EXO_CUSTOMER2:
    tabTextColor = "var(--maincolor-cmc)";
    break;
  default:
    tabTextColor = "var(--maincolor)";
    break;
}

const TabComponent = ({ formArray, pid, title, setFormDetail, selectedFormDetail }) => {
  const [value, setValue] = useState(0);
  const tabListRef = useRef(null);
  const selectedTabRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Set the selected tab based on the selected form
  useEffect(() => {
    const selectedFormIndex = formArray.findIndex(
      (form) => form.form_list_id === selectedFormDetail?.form_list_id || formArray[0]?.form_list_id
    );
    setValue(selectedFormIndex);
  }, [selectedFormDetail, formArray]);

  useEffect(() => {
    if (selectedTabRef.current) {
      selectedTabRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [value]);

  // Scroll handling for left and right scroll arrows
  useEffect(() => {
    const handleScroll = () => {
      if (tabListRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
      }
    };

    handleScroll();

    tabListRef.current.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll); // To handle resizing effects

    return () => {
      tabListRef.current.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // Find icons to display with the tab
  const findIcon = (id) => {
    return formFixture(undefined).find((item) => id === item.formListId);
  };

  // Function to scroll tabs left or right
  const scrollTabs = (direction) => {
    if (tabListRef.current) {
      const scrollAmount = 100; // Amount to scroll on each click
      tabListRef.current.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div
      className="tab-container"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        marginTop: "8px",
      }}
    >
      {/* Left Scroll Arrow */}
      <button
        className="scroll-arrow left-arrow"
        onClick={() => scrollTabs("left")}
        disabled={!canScrollLeft}
        style={{ color: canScrollLeft ? "var(--maincolor)" : "#e3e1e1" }}
      >
        <ArrowBackIosNewIcon />
      </button>

      {/* Wrapper for Tabs with Gray Background */}
      <div
        className="tabs-background" // Added background container for tabs
        style={{
          display: "flex",
          flexGrow: 1,
          backgroundColor: "#f0f0f0", // Gray background for the entire tab section
          padding: "6px",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <ul
          className="nav nav-tabs side-tabs"
          id="myTab"
          role="tablist"
          ref={tabListRef}
          style={{
            display: "flex",
            flexWrap: "nowrap",
            borderBottom: "none",
            overflowX: "scroll",
            overflowY: "hidden", // Keep horizontal scroll only
            gap: '8px',
            height: '8vh',
          }}
        >
          {formArray.map((form, index) => {
            const isSelected = selectedFormDetail.form_list_id === form.form_list_id;
            return (
              <li className="nav-item" role="presentation" key={index} style={{ flexShrink: 0, margin: '4px' }}>
                <button
                  className={`nav-link ${isSelected ? "active" : ""}`}
                  id={`tab-${index}`}
                  data-bs-toggle="tab"
                  data-bs-target={`#content-${index}`}
                  type="button"
                  role="tab"
                  aria-controls={`content-${index}`}
                  aria-selected={isSelected ? "true" : "false"}
                  onClick={() => {
                    setFormDetail(form);
                    setValue(index);
                  }}
                  ref={isSelected ? selectedTabRef : null}
                  style={{
                    whiteSpace: "nowrap",
                    textTransform: "unset",
                    fontFamily: "Poppins, sans-serif",
                    fontWeight: isSelected ? "600" : "400",
                    letterSpacing: "-0.4px",
                    opacity: 1,
                    padding: "0.75rem 0.875rem",
                    borderRadius: "4px", // Pill-shaped tabs
                    transition: "all 0.3s ease-in-out", // Smooth transition
                    // border: `2px solid ${isSelected ? "#87b1cd" : "#d2d2d2"}`, // Border color
                    backgroundColor: isSelected ? "#FFFFFF" : "#e6e3e3", // Gray background for inactive tabs
                    boxShadow: isSelected ? "rgba(0, 0, 0, 0.26) 0px 0px 8px" : "none",
                    color: isSelected ? "#312b6b" : "black", // Text color change
                    minHeight: '44px',
                    cursor: "pointer",
                  }}
                >
                  {findIcon(form.form_list_id) && (
                    <span style={{ color: isSelected ? "white" : "black" }}>
                      {findIcon(form.form_list_id).icon}
                    </span>
                  )}&ensp;

                  {form.form_display_title}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right Scroll Arrow */}
      <button
        className="scroll-arrow right-arrow"
        onClick={() => scrollTabs("right")}
        disabled={!canScrollRight}
        style={{ color: canScrollRight ? "var(--maincolor)" : "#e3e1e1" }}
      >
        <ArrowForwardIosIcon />
      </button>

      {/* Tab Content */}
      <div className="tab-content" id="myTabContent">
        {formArray.map((form, index) => {
          const isSelected = value === index;
          return (
            <div
              key={index}
              className={`tab-pane fade ${isSelected ? "show active" : ""}`}
              id={`content-${index}`}
              role="tabpanel"
              aria-labelledby={`tab-${index}`}
            >
              {/* Content related to the tab */}
            </div>
          );
        })}
      </div>

      {/* CSS for scroll arrows */}
      <style jsx>{`
        .scroll-arrow {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default TabComponent;
